import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PatientsNetworkRow, PatientsNetworkSortKey } from '@/application/metrics/patientsNetworkRanking';
import {
  patientsNetworkExtremes,
  sortPatientsNetworkRows,
} from '@/application/metrics/patientsNetworkRanking';
import { ResponsiveDataList } from '@/presentation/components/common/ResponsiveDataList';
import { formatInteger, formatPercent } from '@/presentation/components/metrics/metricFormatters';

interface PatientsNetworkRankingProps {
  rows: PatientsNetworkRow[];
  locale: string;
  onOpenClinic: (tenantId: string) => void;
}

export function PatientsNetworkRanking({ rows, locale, onOpenClinic }: PatientsNetworkRankingProps) {
  const { t } = useTranslation();
  const [sortKey, setSortKey] = useState<PatientsNetworkSortKey>('noReturn');
  const [sortDesc, setSortDesc] = useState(true);

  const ranked = useMemo(
    () => sortPatientsNetworkRows(rows, sortKey, sortDesc),
    [rows, sortKey, sortDesc]
  );
  const { worstTenantId, bestTenantId } = useMemo(() => patientsNetworkExtremes(rows), [rows]);
  const worst = ranked.find((row) => row.tenantId === worstTenantId) ?? null;

  const toggleSort = (key: PatientsNetworkSortKey) => {
    if (sortKey === key) {
      setSortDesc((value) => !value);
      return;
    }
    setSortKey(key);
    setSortDesc(key !== 'name' && key !== 'tasa');
  };

  const badge = (tenantId: string) => {
    if (tenantId === worstTenantId) {
      return (
        <span className="ml-1.5 rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-800 dark:bg-red-950/60 dark:text-red-200">
          {t('domain.patients.network.worst')}
        </span>
      );
    }
    if (tenantId === bestTenantId) {
      return (
        <span className="ml-1.5 rounded-full bg-teal-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-teal-800 dark:bg-teal-950/60 dark:text-teal-200">
          {t('domain.patients.network.best')}
        </span>
      );
    }
    return null;
  };

  return (
    <section className="space-y-3" data-testid="patients-network-ranking">
      <div>
        <h2 className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
          {t('domain.patients.network.title')}
        </h2>
        <p className="mt-1 max-w-2xl text-sm text-slate-600 dark:text-slate-300">
          {t('domain.patients.network.hint')}
        </p>
      </div>

      {worst ? (
        <button
          type="button"
          data-testid="patients-network-worst-link"
          onClick={() => onOpenClinic(worst.tenantId)}
          className="text-sm font-semibold text-teal-700 hover:underline dark:text-teal-300"
        >
          {t('domain.patients.network.openWorst', { clinic: worst.name })} →
        </button>
      ) : null}

      <ResponsiveDataList
        rows={ranked}
        keyField={(row) => row.tenantId}
        emptyMessage={t('domain.patients.network.empty')}
        onRowClick={(row) => onOpenClinic(row.tenantId)}
        columns={[
          {
            key: 'clinic',
            header: (
              <button type="button" className="font-semibold" onClick={() => toggleSort('name')}>
                {t('domain.patients.network.cols.clinic')}
              </button>
            ),
            cell: (row) => (
              <span className="font-semibold text-gray-900 dark:text-white">
                {row.name}
                {badge(row.tenantId)}
              </span>
            ),
          },
          {
            key: 'tasa',
            align: 'right',
            header: (
              <button type="button" className="font-semibold" onClick={() => toggleSort('tasa')}>
                {t('domain.patients.network.cols.tasa')}
              </button>
            ),
            cell: (row) =>
              row.tasaRecurrencia == null ? '—' : formatPercent(row.tasaRecurrencia, locale),
          },
          {
            key: 'noReturn',
            align: 'right',
            header: (
              <button type="button" className="font-semibold" onClick={() => toggleSort('noReturn')}>
                {t('domain.patients.network.cols.noReturn')}
              </button>
            ),
            cell: (row) => (row.noReturnTotal == null ? '—' : formatInteger(row.noReturnTotal, locale)),
          },
          {
            key: 'noReturnRate',
            align: 'right',
            header: (
              <button type="button" className="font-semibold" onClick={() => toggleSort('noReturnRate')}>
                {t('domain.patients.network.cols.noReturnRate')}
              </button>
            ),
            cell: (row) =>
              row.noReturnRatePct == null ? '—' : formatPercent(row.noReturnRatePct, locale),
          },
        ]}
        renderCard={(row) => (
          <div className="space-y-1">
            <p className="font-semibold text-gray-900 dark:text-white">
              {row.name}
              {badge(row.tenantId)}
            </p>
            <p className="text-sm tabular-nums text-slate-600 dark:text-slate-300">
              {t('domain.patients.network.cols.tasa')}:{' '}
              {row.tasaRecurrencia == null ? '—' : formatPercent(row.tasaRecurrencia, locale)}
            </p>
            <p className="text-sm tabular-nums text-slate-600 dark:text-slate-300">
              {t('domain.patients.network.cols.noReturn')}:{' '}
              {row.noReturnTotal == null ? '—' : formatInteger(row.noReturnTotal, locale)}
            </p>
          </div>
        )}
      />
    </section>
  );
}
